<template>
  <div
    class="mastery-sidebar-component"
    :class="{ v2: questionUiV2Enabled }"
  >
    <ul class="nav-items-container">
      <li class="flex-1 desktop-only" />
      <li
        v-for="navItem in navigationItems"
        :key="navItem.id"
        :class="['nav-item', { active: navItemActive(navItem) }]"
      >
        <router-link
          v-disableable="{blur: onTour, dim: onTour, disabled: onTour }"
          :to="navItem.route"
          class="nav-item-link"
          @click="navItemClicked(navItem)"
        >
          <img
            :src="navItemIcon(navItem)"
            class="nav-item-icon"
            alt=""
            role="presentation"
          >
          <span class="nav-item-name">
            {{ navItem.name }}
          </span>
        </router-link>
      </li>
      <li class="nav-item question-sets-nav-item">
        <question-sets-sidebar-link rail />
      </li>
      <li class="nav-item study-planner-nav-item">
        <study-planner-sidebar-link rail />
      </li>
      <li class="nav-item tag-search-nav-item">
        <tag-search-sidebar-link rail />
      </li>
      <li class="nav-item discussion-board-nav-item">
        <discussion-board-sidebar-link rail />
      </li>
      <li class="flex-1 desktop-only" />
      <li
        v-if="shouldShowRateLimit"
        class="nav-item desktop-only button-wrapper"
      >
        <span class="nav-item-link">
          <rate-limit-counter-button />
          <span class="nav-item-name text-xs">
            <span class="font-medium">Daily Free PQs</span><br />
            <span>{{ todaysDateLocal }}</span>
          </span>
        </span>
      </li>
    </ul>
  </div>
</template>

<script>
import DashboardMixin from '@/mixins/dashboard';
import NavItemsMixin from '@/mixins/navItems';
import QuestionsUIV2Mixin from '@/mixins/questions-ui-v2';
import QuestionSetsSidebarLink from '@/components/question-sets/SidebarLink.vue';
import StudyPlannerSidebarLink from '@/components/study-planner/SidebarLink.vue';
import DiscussionBoardSidebarLink from '@/components/discussion-board/SidebarLink.vue';
import TagSearchSidebarLink from '@/components/tag-search/SidebarLink.vue';
import RateLimitCounterButton from '@/components/singletons/RateLimitCounterButton.vue';
import RateLimitMixin from '@/mixins/rate-limit';

export default {
  name: 'MasterySidebar',
  components: {
    QuestionSetsSidebarLink,
    StudyPlannerSidebarLink,
    DiscussionBoardSidebarLink,
    TagSearchSidebarLink,
    RateLimitCounterButton,
  },
  mixins: [DashboardMixin, NavItemsMixin, QuestionsUIV2Mixin, RateLimitMixin],
  data() {
    return {
      todaysDateLocal: this.getTodaysDateLocal(),
      millisecondsUntilMidnight: this.getMillisecondsUntilMidnight(),
      timer: null,
    };
  },
  computed: {
    active() {
      // if route has no ties to web-main group, don't do anything
      // secondaryRoute on meta just means it doesn't live in main content
      if (this.$route.meta.secondaryRoute) return {};
      // see if one of the items matches query directly first
      const queryItem = this.navigationItems.find(ni => ni.matchesRouteQuery(this.$route));
      if (queryItem) return queryItem;

      // otherwise see if last visited dashboard has a matching menu item
      const lastDashboardIds = this.$store.getters['dashboards/lastVisitedId'];
      const dashboardItemMatch = this.navigationItems.find(ni => {
        // Dashboard group ids can be in the lastVisitedId array so we want to check
        // if the first item in the lastVisitedId array is the id of a dashboard group
        // that is found in the main nav. This will make sure that we are only matching
        // the ids of the dashboard group with the root level id in lastDashboardIds to
        // prevent checking dashboard ids against dashboard group ids.
        if (ni.destination === 'dashboard_group') {
          return lastDashboardIds[0] === ni.resourceId;
        }

        return ni.destination === 'dashboard' && lastDashboardIds.includes(ni.resourceId);
      });

      if (dashboardItemMatch) return dashboardItemMatch;

      // otherwise see if there's a home menu item that matches the last dashboard item
      const homeDashboard = this.$store.getters['dashboards/menuDefaultDashboard'];
      if (homeDashboard) {
        const homeItemMatch = this.navigationItems.find(ni => {
          return ni.destination === 'dashboard' && ni.resourceId === homeDashboard.id;
        });

        if (homeItemMatch) return homeItemMatch;
      }

      // lastly see if there's a home item and our path is /
      if (this.$route.path === '/') {
        const homeItemPathMatch = this.navigationItems.find(ni => {
          return ni.destination === 'home';
        });

        if (homeItemPathMatch) return homeItemPathMatch;
      }

      return {};
    },
    onTour() {
      return this.$store.getters['session/onTour'];
    },
  },
  mounted() {
    this.atMidnight();
  },
  beforeUnmount() {
    if (this.timer) {
      clearTimeout(this.timer);
    }
  },
  methods: {
    atMidnight() {
      this.todaysDateLocal = this.getTodaysDateLocal();
      this.millisecondsUntilMidnight = this.getMillisecondsUntilMidnight() + 1000;

      this.timer = setTimeout(() => {
        this.atMidnight();
      }, this.millisecondsUntilMidnight);
    },
    getMillisecondsUntilMidnight() {
      const now = new Date();
      const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
      return midnight - now;
    },
    getTodaysDateLocal() {
      return new Date().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.mastery-sidebar-component {
  position: fixed;
  background-color: #f7f7f7;
  width: rem-calc(88);
  top: rem-calc(48);
  transition: .1s linear width;
  bottom: 0;
  z-index: 900;

  &.v2 {
    background-color: white;
    border-right: 1px solid #E3E8EF;
  }

  .nav-items-container {
    padding: 0;
    margin: 0;
    list-style-type: none;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 100%;

    .button-wrapper {
      padding-bottom: rem-calc(58);

      .nav-item-name {
        margin-top: rem-calc(6);
      }
    }

    .nav-item {
      margin: rem-calc(12) 0;

      .nav-item-link {
        display: block;
        width: 100%;
        height: rem-calc(40);
        padding: rem-calc(8) 0;

        .nav-item-icon {
          display: block;
          height: rem-calc(24);
          width: rem-calc(24);
          margin: 0 auto;
          margin-bottom: rem-calc(8);
          object-fit: cover;
          transition: .25s linear all;;
        }

        .nav-item-name {
          display: none;
          text-align: center;
          color: #000000;
          font-size: rem-calc(12);
        }
      }

      &.active {
        .nav-item-name {
          font-weight: $semi-bold;
        }
      }
    }
  }

  // The link hides itself when the user can't see question sets, so drop the empty slot
  .question-sets-nav-item:empty,
  .study-planner-nav-item:empty,
  .tag-search-nav-item:empty,
  .discussion-board-nav-item:empty {
    display: none;
  }

  &:hover {
    width: rem-calc(132);

    :deep(.question-sets-sidebar-link.rail .name),
    :deep(.study-planner-sidebar-link.rail .name),
    :deep(.tag-search-sidebar-link.rail .name),
    :deep(.discussion-board-sidebar-link.rail .name) {
      display: block;
    }

    .nav-items-container {
      .nav-item {
        .nav-item-link {
          .nav-item-name {
            display: block;
          }
        }
      }
    }
  }

  @include breakpoint(small) {
    display: none;
  }
}
</style>
